import { Component } from '@theme/component';
import { QuantitySelectorUpdateEvent } from '@theme/events';

const MAX_QUANTITY = 300;

export class QuantitySelectorComponent extends Component {
  requiredRefs = ['quantityInput', 'minusButton', 'plusButton'];
  serverDisabledMinus = false;
  serverDisabledPlus = false;
  initialized = false;

  connectedCallback() {
    super.connectedCallback();

    const { minusButton, plusButton, quantityInput } = this.refs;

    quantityInput.min = '1';
    quantityInput.max = MAX_QUANTITY.toString();
    quantityInput.step = '1';

    if (minusButton.disabled) {
      this.serverDisabledMinus = true;
    }

    if (plusButton.disabled) {
      this.serverDisabledPlus = true;
    }

    this.initialized = true;
    this.updateButtonStates();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
  }

  setCartQuantity(cartQty) {
    this.refs.quantityInput.setAttribute('data-cart-quantity', cartQty.toString());
    this.updateCartQuantity();
  }

  canAddToCart() {
    const { cartQuantity, value } = this.getCurrentValues();

    return {
      canAdd: true,
      maxQuantity: MAX_QUANTITY,
      cartQuantity,
      quantityToAdd: value,
    };
  }

  getValue() {
    return this.refs.quantityInput.value;
  }

  setValue(value) {
    this.refs.quantityInput.value = value;
  }

  updateConstraints(min, max, step) {
    const { quantityInput } = this.refs;
    const currentValue = parseInt(quantityInput.value) || 1;

    quantityInput.min = '1';
    quantityInput.max = MAX_QUANTITY.toString();
    quantityInput.step = '1';

    const newValue = Math.max(1, Math.min(MAX_QUANTITY, currentValue));

    if (newValue !== currentValue) {
      quantityInput.value = newValue.toString();
    }

    this.updateButtonStates();
  }

  getCurrentValues() {
    const { quantityInput } = this.refs;

    return {
      min: 1,
      max: MAX_QUANTITY,
      step: 1,
      value: parseInt(quantityInput.value) || 1,
      cartQuantity: parseInt(quantityInput.getAttribute('data-cart-quantity') || '0'),
    };
  }

  getEffectiveMax() {
    return MAX_QUANTITY;
  }

  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    if (!this.serverDisabledMinus) {
      minusButton.disabled = value <= min;
    }

    if (!this.serverDisabledPlus) {
      plusButton.disabled = value >= effectiveMax;
    }
  }

  updateQuantity(stepMultiplier) {
    const { quantityInput } = this.refs;
    const { min, step, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    const newValue = Math.min(
      effectiveMax,
      Math.max(min, value + step * stepMultiplier)
    );

    quantityInput.value = newValue.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  increaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(1);
  }

  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;
    event.preventDefault();
    this.updateQuantity(-1);
  }

  selectInputValue(event) {
    const { quantityInput } = this.refs;
    if (!(event.target instanceof HTMLInputElement) || document.activeElement !== quantityInput) return;

    quantityInput.select();
  }

  setQuantity(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    event.preventDefault();

    const { quantityInput } = this.refs;
    const quantity = Math.min(
      MAX_QUANTITY,
      Math.max(1, parseInt(event.target.value) || 1)
    );

    quantityInput.value = quantity.toString();
    this.onQuantityChange();
    this.updateButtonStates();
  }

  onQuantityChange() {
    const { quantityInput } = this.refs;
    const newValue = parseInt(quantityInput.value);

    quantityInput.dispatchEvent(new QuantitySelectorUpdateEvent(newValue, Number(quantityInput.dataset.cartLine)));
  }

  updateCartQuantity() {
    const { quantityInput } = this.refs;
    const { value } = this.getCurrentValues();

    const clampedValue = Math.min(
      MAX_QUANTITY,
      Math.max(1, value)
    );

    if (clampedValue !== value) {
      quantityInput.value = clampedValue.toString();
    }

    this.updateButtonStates();
  }

  get quantityInput() {
    if (!this.refs.quantityInput) {
      throw new Error('Missing <input ref="quantityInput" /> inside <quantity-selector-component />');
    }

    return this.refs.quantityInput;
  }
}

if (!customElements.get('quantity-selector-component')) {
  customElements.define('quantity-selector-component', QuantitySelectorComponent);
}